// Import the necessary types
import { useState, useEffect, useRef } from 'react';
import { Language, InsuranceAnalysisResult } from '@/types/insurance';
import { useAuth } from '@/context/AuthContext';
import { useToast } from "@/hooks/use-toast";
import { useSearchParams } from 'react-router-dom';
import { detectLanguage } from '@/lib/languageUtils';
import { v4 as uuidv4 } from 'uuid';
import { createComparison, createOffer } from '@/lib/comparisonHandler';

// Find and fix the language type issue:
// We'll replace the direct manipulation with a proper function

// Should be changed to:
// Assuming lang is of type string and needs to be explicitly cast to Language type
const setLanguageWithValidation = (lang: string, setDetectedLanguage: React.Dispatch<React.SetStateAction<Language>>) => {
  if (lang === 'de' || lang === 'en' || lang === 'fr' || lang === 'it') {
    setDetectedLanguage(lang as Language);
  } else {
    setDetectedLanguage('de'); // Default to German if not a valid language
  }
};

// The rest of your useBuilderState hook
export const useBuilderState = () => {
  const [view, setView] = useState<'preview' | 'code'>('preview');
  const [emailContent, setEmailContent] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [initialPrompt, setInitialPrompt] = useState<string | null>(localStorage.getItem('currentPrompt'));
  const [isInsuranceOffer, setIsInsuranceOffer] = useState<boolean>(false);
  const [isInsuranceComparison, setIsInsuranceComparison] = useState<boolean>(false);
  const [analysisResult, setAnalysisResult] = useState<InsuranceAnalysisResult | null>(null);
  const [activePanel, setActivePanel] = useState<'chat' | 'history'>('chat');
  const [isOfferModalOpen, setIsOfferModalOpen] = useState<boolean>(false);
  const [offerUrl, setOfferUrl] = useState<string | null>(null);
  const [offerTitle, setOfferTitle] = useState<string | null>(null);
  const [detectedLanguage, setDetectedLanguage] = useState<Language>('de');
  const [currentChatId, setCurrentChatId] = useState<string | undefined>(localStorage.getItem('currentChatId'));
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [streamingCode, setStreamingCode] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const lang = localStorage.getItem('preferredLanguage') || detectLanguage(initialPrompt || '') || 'de';
    setLanguageWithValidation(lang, setDetectedLanguage);
  }, [initialPrompt]);

  useEffect(() => {
    const storedIsGenerating = localStorage.getItem('isGenerating');
    if (storedIsGenerating === 'true') {
      setIsGenerating(true);
    }
  }, []);

  useEffect(() => {
    const offerUrlParam = searchParams.get('offerUrl');
    const offerTitleParam = searchParams.get('offerTitle');
    if (offerUrlParam && offerTitleParam) {
      setOfferUrl(offerUrlParam);
      setOfferTitle(offerTitleParam);
      setIsOfferModalOpen(true);
      
      window.dispatchEvent(new CustomEvent('suggest-email-template', {
        detail: {
          offerUrl: offerUrlParam,
          offerTitle: offerTitleParam
        }
      }));
    }
  }, [searchParams]);

  const handleEmailGenerated = (content: string) => {
    setEmailContent(content);
    setIsInsuranceOffer(false);
    setIsInsuranceComparison(false);
    setAnalysisResult(null);
  };

  const handleStreamingCodeUpdate = (html: string) => {
    setStreamingCode(html);
  };

  const handleStreamingComplete = (finalHtml: string) => {
    setStreamingCode(finalHtml);
  };

  const clearPreview = () => {
    setEmailContent('');
    setIsInsuranceOffer(false);
    setIsInsuranceComparison(false);
    setAnalysisResult(null);
  };

  return {
    view,
    setView,
    emailContent,
    isGenerating,
    setIsGenerating,
    initialPrompt,
    isInsuranceOffer,
    isInsuranceComparison,
    analysisResult,
    activePanel,
    setActivePanel,
    containerRef,
    isOfferModalOpen,
    setIsOfferModalOpen,
    offerUrl,
    offerTitle,
    detectedLanguage,
    currentChatId,
    isStreaming,
    streamingCode,
    handleEmailGenerated,
    handleStreamingCodeUpdate,
    handleStreamingComplete,
    clearPreview,
    user
  };
};
